'use client'

import { useLocale } from 'next-intl'
import { useEffect } from 'react'

import { useApiMutation } from '@/hooks/use-api'
import { useAuthContext } from '@/hooks/use-auth-context'
import type { UIPreferenceResponse } from '@/hooks/use-creative-winners'

const PREFERENCE_URL = '/api/users/me/ui-preferences/ui_locale'
const SYNCED_KEY_PREFIX = 'nex-ui-locale-synced:'

/**
 * Mirror the chat UI locale (NEXT_LOCALE cookie) into the user's server-side
 * preferences, so turns the server starts on its own — automation runs,
 * autopilot wakes, action-review recovery — know which language the user
 * reads. Runs once per mount; `setLocaleCookie` reloads the page on a switch,
 * so one mount-time effect covers both "first visit" and "changed language".
 *
 * The dedupe marker is keyed by user id: on a shared browser a second person
 * signing in with the same UI locale still gets their own preference written.
 */
export function useUiLocaleSync(): void {
  const locale = useLocale()
  const userId = useAuthContext().user?.id ?? null
  const { trigger } = useApiMutation<UIPreferenceResponse, { value: { locale: string } }>(
    PREFERENCE_URL,
    { method: 'PATCH', suppressGlobalErrorHandler: true },
  )

  useEffect(() => {
    if (!userId) return
    const key = `${SYNCED_KEY_PREFIX}${userId}`
    let synced: string | null = null
    try {
      synced = window.localStorage.getItem(key)
    } catch {
      // Storage blocked: fall through and sync every mount.
    }
    if (synced === locale) return
    void trigger({ body: { value: { locale } } })
      .then(() => {
        try {
          window.localStorage.setItem(key, locale)
        } catch {
          // Best effort; the next mount just PATCHes again.
        }
      })
      .catch(() => {
        // Offline or the session just ended — nothing to do, the next mount retries.
      })
  }, [locale, trigger, userId])
}
